<template>
  <div class="h-screen w-full bg-white flex flex-col">
    <div class="flex-shrink-0 h-14 relative" style="z-index: 60;">
      <nav-menu v-if="isAdmin" />
      <div v-else class="h-14 bg-secondary text-white flex items-center px-4">
        <button @click="$router.go(-1)" class="mr-3 text-gray-400 hover:text-white">
          <i class="fa-solid fa-arrow-left"></i>
        </button>
        <h1 class="text-lg font-bold">Team Tracker</h1>
      </div>
    </div>

    <div class="flex flex-grow overflow-hidden" style="min-height: 0">
      <!-- Side Panel -->
      <div :class="[
        'bg-secondary text-white flex flex-col overflow-hidden transition-all duration-300 flex-shrink-0',
        'md:relative md:z-auto absolute z-50 h-full',
        sidebarOpen ? 'w-80' : 'w-0'
      ]">
        <div v-show="sidebarOpen" class="w-80 flex flex-col h-full">
        <div class="p-4 border-b border-gray-700 flex items-center">
          <div class="flex-grow">
            <h2 class="header-bold text-lg">Vehicles</h2>
            <p class="text-xs text-gray-400 mt-1">{{ onlineVehicles.length }} online / {{ vehiclesWithJobs.length }} total</p>
          </div>
          <button @click="sidebarOpen = false" class="md:hidden w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-white hover:bg-gray-700">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>

        <div class="flex-grow overflow-y-auto">
          <div
            v-for="v in vehiclesWithJobs"
            :key="v.id"
            @click="centerOnVehicle(v)"
            :class="[
              'px-4 py-3 cursor-pointer border-b border-gray-700 hover:bg-gray-700 transition-colors',
              selectedVehicleId === v.id ? 'bg-gray-700' : ''
            ]"
          >
            <div class="flex items-center">
              <div
                :class="[
                  'w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0',
                  isTeamLeader ? '' : vehicleBgClass(v)
                ]"
                :style="isTeamLeader ? { background: testerColor(v) } : null"
              >
                {{ initials(v.assignedUserName || '?') }}
              </div>
              <div class="ml-3 flex-grow min-w-0">
                <div class="header-bold text-sm truncate">{{ v.assignedUserName || v.name || v.id }}</div>
                <div class="text-xs text-gray-400 truncate">
                  {{ v._currentJob ? (v._currentJob.company || v._currentJob.projectName || v._currentJob.projectId) : 'No active job' }}
                </div>
              </div>
            </div>

            <div class="flex items-center mt-2 ml-13">
              <span
                :class="[
                  'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
                  stagePillClass(v._jobStage)
                ]"
              >
                <span :class="['w-1.5 h-1.5 rounded-full mr-1.5', stageDotClass(v._jobStage)]"></span>
                {{ stageLabel(v._jobStage) }}
              </span>
              <span v-if="v._eta" class="text-xs text-gray-500 ml-auto">
                ETA {{ v._eta }} min
              </span>
            </div>

            <!-- Milestone indicators -->
            <div v-if="v._jobStage !== 'idle' && v._jobStage !== 'offline'" class="flex mt-2 space-x-1">
              <div
                v-for="(ms, i) in milestones(v._jobStage)"
                :key="i"
                :class="['h-1.5 flex-grow rounded-full', ms.active ? ms.color : 'bg-gray-600']"
                :title="ms.label"
              ></div>
            </div>
          </div>
        </div>

        <!-- Legend -->
        <!-- Team leader markers are coloured per tester, so the legend lists testers -->
        <div v-if="isTeamLeader" class="p-4 border-t border-gray-700">
          <div class="text-xs text-gray-400 mb-2 header-bold">Testers</div>
          <div class="grid grid-cols-2 gap-1">
            <div v-for="v in vehiclesWithJobs" :key="'legend-' + v.id" class="flex items-center text-xs min-w-0">
              <span class="w-2.5 h-2.5 rounded-full mr-1.5 flex-shrink-0" :style="{ background: testerColor(v) }"></span>
              <span class="truncate">{{ v.assignedUserName || v.name || v.id }}</span>
            </div>
          </div>
        </div>
        <div v-else class="p-4 border-t border-gray-700">
          <div class="text-xs text-gray-400 mb-2 header-bold">Status</div>
          <div class="grid grid-cols-2 gap-1">
            <div class="flex items-center text-xs"><span class="w-2.5 h-2.5 rounded-full bg-blue-500 mr-1.5"></span>En Route</div>
            <div class="flex items-center text-xs"><span class="w-2.5 h-2.5 rounded-full bg-yellow-500 mr-1.5"></span>Arrived</div>
            <div class="flex items-center text-xs"><span class="w-2.5 h-2.5 rounded-full bg-purple-500 mr-1.5"></span>Testing</div>
            <div class="flex items-center text-xs"><span class="w-2.5 h-2.5 rounded-full bg-green-500 mr-1.5"></span>Completed</div>
          </div>
        </div>
        </div>
      </div>

      <!-- Map -->
      <div class="flex-grow relative">
        <div ref="mapContainer" class="w-full h-full"></div>

        <!-- Mobile sidebar toggle (visible when sidebar is closed) -->
        <button v-if="!sidebarOpen"
          @click="sidebarOpen = true"
          class="md:hidden absolute top-3 right-3 z-50 w-10 h-10 bg-secondary bg-opacity-90 text-white rounded-lg shadow-lg flex items-center justify-center hover:bg-opacity-100">
          <i class="fa-solid fa-users"></i>
          <span v-if="onlineVehicles.length" class="absolute -top-1 -right-1 w-5 h-5 bg-primary rounded-full text-xs flex items-center justify-center font-bold">{{ onlineVehicles.length }}</span>
        </button>

        <!-- Retro Radio Dial — visible in History mode -->
        <div v-if="historyMode" class="radio-dial absolute top-0 left-0 right-0 z-40">
          <div class="radio-dial-inner">
            <button @click="toggleHistoryPlay" class="radio-dial-play-btn" :disabled="historyLoading" :style="historyLoading ? 'opacity:0.4;cursor:wait' : ''">
              <i v-if="historyLoading" class="fa-solid fa-spinner fa-spin"></i>
              <i v-else :class="historyPlaying ? 'fa-solid fa-pause' : 'fa-solid fa-play'"></i>
            </button>
            <div class="radio-dial-track" ref="dialTrack"
                 @mousedown="onDialMouseDown($event)"
                 @touchstart.prevent="onDialMouseDown($event)">
              <template v-for="tick in historyHourTicks">
                <div :key="'t-'+tick.label" class="radio-tick" :style="{ left: tick.pct + '%' }"></div>
                <span :key="'l-'+tick.label" class="radio-tick-label" :style="{ left: tick.pct + '%' }">{{ tick.label }}</span>
              </template>
              <div class="radio-needle" :style="{ left: (historySliderValue / 10) + '%' }"></div>
              <!-- Loading overlay on dial track -->
              <div v-if="historyLoading" class="radio-dial-loading-overlay">
                <div class="radio-dial-loading-bar"></div>
                <span class="radio-dial-loading-text">Loading vehicle history&hellip;</span>
              </div>
            </div>
            <div class="radio-dial-right">
              <div style="display:flex;align-items:center;gap:8px">
                <input type="date" v-model="historyDate" @change="onHistoryDateChange"
                  class="history-date-picker" :disabled="historyLoading" />
                <div class="radio-time-display">{{ historyLoading ? '--:--' : historyTimeFormatted }}</div>
              </div>
              <button @click="exitHistoryMode" class="radio-live-btn">
                <span class="radio-live-dot"></span>
                LIVE
              </button>
            </div>
          </div>
        </div>

        <!-- Toolbar buttons -->
        <div class="absolute left-3 z-50 flex items-center space-x-2" :style="{ top: historyMode ? '64px' : '12px' }">
          <button
            @click="toggleSearch"
            :disabled="historyMode"
            :class="[
              'bg-gray-900 bg-opacity-90 text-white px-3 py-2 rounded-lg shadow-lg border transition-all text-xs flex items-center space-x-2',
              historyMode ? 'opacity-40 cursor-not-allowed border-gray-700' : (searchOpen ? 'border-primary hover:bg-opacity-100' : 'border-gray-700 hover:border-primary hover:bg-opacity-100')
            ]"
          >
            <i :class="['fa-solid fa-magnifying-glass', searchOpen && !historyMode ? 'text-primary' : 'text-gray-400']"></i>
            <span>Search</span>
          </button>
          <button
            @click="historyMode ? exitHistoryMode() : enterHistoryMode()"
            :disabled="searchOpen || !!searchLocation"
            :class="[
              'bg-gray-900 bg-opacity-90 text-white px-3 py-2 rounded-lg shadow-lg border transition-all text-xs flex items-center space-x-2',
              (searchOpen || searchLocation) ? 'opacity-40 cursor-not-allowed border-gray-700' : (historyMode ? 'border-primary hover:bg-opacity-100' : 'border-gray-700 hover:border-primary hover:bg-opacity-100')
            ]"
          >
            <i v-if="historyLoading" class="fa-solid fa-spinner fa-spin text-primary"></i>
            <i v-else :class="['fa-solid fa-clock-rotate-left', historyMode ? 'text-primary' : 'text-gray-400']"></i>
            <span>{{ historyLoading ? 'Loading...' : (historyMode ? 'Exit History' : 'History') }}</span>
          </button>
        </div>

        <!-- Location Search Box (Mapbox Geocoder) — centered on screen with glow backdrop -->
        <div v-show="searchOpen && !historyMode" class="absolute inset-0 z-40 flex items-center justify-center" style="pointer-events: none;">
          <div class="geocoder-backdrop" style="pointer-events: auto;">
            <div ref="geocoderContainer" class="w-96 geocoder-container"></div>
          </div>
        </div>

        <!-- Driver Distance Results Panel -->
        <div v-if="searchLocation && !historyMode" class="absolute left-1/2 transform -translate-x-1/2 z-30 w-96" style="top: 56px;">
          <div class="bg-gray-900 bg-opacity-95 rounded-lg border border-gray-700 shadow-xl overflow-hidden">
            <div class="px-4 py-2 border-b border-gray-700 flex items-center justify-between">
              <div>
                <div class="text-white text-sm font-bold">
                  <i class="fa-solid fa-location-dot text-primary mr-1"></i>
                  {{ searchLocation.name }}
                </div>
                <div class="text-xs text-gray-500">{{ searchResultsOnline.length }} drivers available</div>
              </div>
              <span v-if="searchLoading" class="text-xs text-gray-500">
                <i class="fa-solid fa-spinner fa-spin mr-1"></i>Calculating...
              </span>
            </div>
            <div class="max-h-80 overflow-y-auto">
              <div
                v-for="(r, i) in searchResultsOnline"
                :key="r.vehicleId"
                @click="centerOnVehicle(r.vehicle)"
                class="px-4 py-2 border-b border-gray-800 hover:bg-gray-800 cursor-pointer"
              >
                <div class="flex items-center">
                  <div class="w-6 h-6 rounded-full bg-gray-700 flex items-center justify-center text-xs text-gray-300 font-bold flex-shrink-0">
                    {{ i + 1 }}
                  </div>
                  <div class="ml-3 flex-grow min-w-0">
                    <div class="text-sm text-white font-medium truncate">{{ r.name }}</div>
                    <div class="text-xs text-gray-500">{{ r.currentLocation }}</div>
                  </div>
                  <div class="ml-3 text-right flex-shrink-0">
                    <div v-if="r.duration != null" class="text-sm font-bold text-white">{{ r.durationText }}</div>
                    <div v-if="r.distance != null" class="text-xs text-gray-500">{{ r.distanceText }}</div>
                    <div v-if="r.loading" class="text-xs text-gray-500"><i class="fa-solid fa-spinner fa-spin"></i></div>
                  </div>
                </div>
                <!-- Enriched job info row -->
                <div class="ml-9 mt-1 flex items-center text-xs">
                  <span v-if="r.jobName" class="text-gray-400 truncate max-w-[140px]">
                    {{ r.jobName }}
                    <span v-if="r.currentJobEta" class="text-gray-500"> &middot; ETA {{ r.currentJobEta }}m</span>
                    <span v-if="r.isLate" class="text-red-400 ml-1">late</span>
                  </span>
                  <span v-else class="text-green-400">No active job</span>
                  <span v-if="r.driverScore != null" class="ml-auto text-gray-500">
                    <i class="fa-solid fa-star text-yellow-500" style="font-size:9px"></i> {{ r.driverScore }}
                  </span>
                </div>
                <div class="ml-9 mt-0.5">
                  <span :class="['text-xs font-medium', r.jobName ? 'text-yellow-400' : 'text-green-400']">
                    {{ r.availabilityText }}
                  </span>
                </div>
              </div>
              <div v-if="searchResultsOnline.length === 0 && !searchLoading" class="px-4 py-3 text-xs text-gray-500 text-center">
                No online drivers with known positions
              </div>
            </div>

            <!-- AI Dispatch Advisor Button -->
            <div v-if="searchResultsOnline.length > 0 && !searchLoading" class="px-4 py-3 border-t border-gray-700">
              <button
                @click="getDispatchRecommendation"
                :disabled="dispatchLoading"
                class="w-full py-2 px-4 rounded-lg text-white text-sm font-medium flex items-center justify-center transition-all"
                :class="dispatchLoading ? 'bg-gray-700 cursor-wait' : 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500'"
              >
                <i :class="['mr-2', dispatchLoading ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-wand-magic-sparkles']"></i>
                {{ dispatchLoading ? 'Analyzing...' : 'AI Dispatch Advisor' }}
              </button>
            </div>

            <!-- AI Recommendation Panel -->
            <div v-if="dispatchRecommendation" class="px-4 py-3 border-t border-gray-700 bg-gray-800 bg-opacity-60" style="max-height: 300px; overflow-y: auto;">
              <div class="flex items-center mb-2 sticky top-0 bg-gray-800 pb-1">
                <i class="fa-solid fa-robot text-purple-400 mr-2 text-xs"></i>
                <span class="text-xs text-purple-300 font-bold">AI Recommendation</span>
                <button @click="dispatchRecommendation = null" class="ml-auto text-gray-500 hover:text-gray-300 text-xs">
                  <i class="fa-solid fa-xmark"></i>
                </button>
              </div>
              <p class="text-xs text-gray-300 leading-relaxed whitespace-pre-wrap">{{ dispatchRecommendation }}</p>
            </div>
          </div>
        </div>

        <!-- Late Arrival Alerts Panel -->
        <div v-if="visibleLateVehicles.length > 0 && !searchLocation && !historyMode" class="absolute left-4 z-10" style="top: 56px;">
          <!-- Collapsed: red icon badge -->
          <div v-if="alertsCollapsed"
               @click="alertsCollapsed = false"
               class="bg-red-900 bg-opacity-90 rounded-lg shadow-lg p-2 cursor-pointer hover:bg-red-800 transition-colors"
               title="Show late alerts">
            <i class="fa-solid fa-triangle-exclamation text-red-400 text-sm"></i>
            <span class="text-xs text-red-300 font-bold ml-1">{{ visibleLateVehicles.length }}</span>
          </div>
          <!-- Expanded: full alert list -->
          <div v-else class="bg-red-900 bg-opacity-90 rounded-lg shadow-lg p-3 max-w-xs">
            <div class="text-xs text-red-300 font-bold mb-2 flex items-center justify-between">
              <span>
                <i class="fa-solid fa-triangle-exclamation mr-1"></i>
                Late Arrival Alerts ({{ visibleLateVehicles.length }})
              </span>
              <button @click="alertsCollapsed = true"
                      class="text-red-400 hover:text-red-200 ml-2 transition-colors"
                      title="Collapse alerts">
                <i class="fa-solid fa-xmark"></i>
              </button>
            </div>
            <div v-for="lv in visibleLateVehicles" :key="lv.id" class="flex items-center text-xs text-red-200 py-1 border-b border-red-800 last:border-0">
              <div class="flex-grow min-w-0">
                <span class="font-bold">{{ lv.assignedUserName }}</span> -
                {{ lv._currentJob ? lv._currentJob.company : '' }}
                <span class="text-red-400 ml-1">+{{ lv._lateBy }} min late</span>
              </div>
              <button @click="dismissAlert(lv.id)"
                      class="flex-shrink-0 ml-2 text-red-500 hover:text-red-200 transition-colors"
                      title="Dismiss alert">
                <i class="fa-solid fa-xmark" style="font-size: 10px;"></i>
              </button>
            </div>
          </div>
        </div>

        <!-- Selected Vehicle Card -->
        <div v-if="selectedVehicle && !searchLocation" class="absolute top-4 right-4 z-10">
          <vehicle-job-card
            :vehicle="selectedVehicle"
            :current-job="selectedVehicle._currentJob"
            :next-job="selectedVehicle._nextJob"
            :eta="selectedVehicle._eta"
            :is-late="selectedVehicle._isLate"
            :late-by="selectedVehicle._lateBy"
            :driver-score="selectedVehicle._driverScore"
            :job-stage="selectedVehicle._jobStage"
            :all-jobs="selectedVehicle._allJobs"
            :day-stats="selectedVehicleDayStats"
            :future-jobs="selectedVehicleFutureJobs"
            :day-path-loading="dayPathLoading"
            :focused-job-id="focusedJob ? focusedJob.id : null"
            :read-only-mode="isTeamLeader"
            :team-members="teamMembers"
            :all-team-leaders="allTeamLeaders"
            @close="exitJobFocus(); selectedVehicleId = null"
            @job-updated="onJobUpdated"
            @focus-job="onFocusJob"
          />
        </div>

        <!-- Job Focus: Back button -->
        <div v-if="focusedJob" class="absolute top-4 left-4 z-50">
          <button
            @click="exitJobFocus"
            class="bg-gray-900 bg-opacity-95 text-white px-4 py-2 rounded-lg shadow-lg border border-primary hover:bg-gray-800 transition-all text-sm flex items-center space-x-2"
          >
            <i class="fa-solid fa-arrow-left text-primary"></i>
            <span>Back to all vehicles</span>
          </button>
        </div>

        <!-- Map Controls -->
        <div class="absolute bottom-6 left-4 z-10 flex items-center space-x-2">
          <!-- Trail Toggle Button -->
          <button
            @click="toggleTrails"
            :class="[
              'bg-gray-900 bg-opacity-90 hover:bg-opacity-100 text-white px-3 py-2 rounded-lg shadow-lg border transition-all text-xs flex items-center space-x-2',
              showTrails ? 'border-primary' : 'border-gray-700 hover:border-primary'
            ]"
          >
            <i :class="['fa-solid fa-route', showTrails ? 'text-primary' : 'text-gray-400']"></i>
            <span>{{ showTrails ? 'Hide Trails' : 'Show Trails' }}</span>
          </button>

          <!-- Features Button -->
          <button
            @click="showFeatures = true"
            class="bg-gray-900 bg-opacity-90 hover:bg-opacity-100 text-white px-3 py-2 rounded-lg shadow-lg border border-gray-700 hover:border-primary transition-all text-xs flex items-center space-x-2"
          >
            <i class="fa-solid fa-bolt text-yellow-400"></i>
            <span>Features</span>
          </button>

          <!-- Setup Help Button -->
          <button
            @click="showSetupHelp = true"
            class="bg-gray-900 bg-opacity-90 hover:bg-opacity-100 text-white px-3 py-2 rounded-lg shadow-lg border border-gray-700 hover:border-primary transition-all text-xs flex items-center space-x-2"
          >
            <i class="fa-solid fa-circle-question text-primary"></i>
            <span>Setup Guide</span>
          </button>
        </div>

        <!-- Features Modal -->
        <div v-if="showFeatures" class="absolute inset-0 z-30 flex items-center justify-center bg-black bg-opacity-60 p-4 overflow-hidden" @click.self="showFeatures = false">
          <div class="bg-gray-900 border border-gray-700 rounded-xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col" style="max-height: calc(100vh - 120px)">
            <!-- Header -->
            <div class="px-6 py-4 border-b border-gray-700 flex items-center justify-between flex-shrink-0">
              <div>
                <h3 class="text-white header-bold text-lg">Tracker Features</h3>
                <p class="text-xs text-gray-400 mt-0.5">Everything this tracker can do</p>
              </div>
              <button @click="showFeatures = false" class="text-gray-500 hover:text-white transition-colors">
                <i class="fa-solid fa-xmark text-lg"></i>
              </button>
            </div>

            <!-- Feature List -->
            <div class="px-6 py-5 space-y-4 overflow-y-auto flex-grow">
              <!-- Live Tracking -->
              <div class="flex items-start space-x-3">
                <div class="w-8 h-8 rounded-lg bg-blue-600 bg-opacity-20 flex items-center justify-center flex-shrink-0">
                  <i class="fa-solid fa-location-crosshairs text-blue-400 text-sm"></i>
                </div>
                <div>
                  <div class="text-white text-sm font-semibold">Live Vehicle Tracking</div>
                  <p class="text-gray-400 text-xs mt-0.5">Real-time GPS positions on an interactive map. Vehicles update automatically as they move. Click any vehicle in the sidebar to centre the map on it.</p>
                </div>
              </div>

              <!-- Closest Driver -->
              <div class="flex items-start space-x-3">
                <div class="w-8 h-8 rounded-lg bg-green-600 bg-opacity-20 flex items-center justify-center flex-shrink-0">
                  <i class="fa-solid fa-magnifying-glass-location text-green-400 text-sm"></i>
                </div>
                <div>
                  <div class="text-white text-sm font-semibold">Find Closest Driver</div>
                  <p class="text-gray-400 text-xs mt-0.5">Search any address using the search bar. All online drivers are ranked by drive time and distance to that location, with real route calculations via Mapbox.</p>
                </div>
              </div>

              <!-- AI Dispatch -->
              <div class="flex items-start space-x-3">
                <div class="w-8 h-8 rounded-lg bg-purple-600 bg-opacity-20 flex items-center justify-center flex-shrink-0">
                  <i class="fa-solid fa-wand-magic-sparkles text-purple-400 text-sm"></i>
                </div>
                <div>
                  <div class="text-white text-sm font-semibold">AI Dispatch Advisor</div>
                  <p class="text-gray-400 text-xs mt-0.5">After searching a location, click "AI Dispatch Advisor" to get an intelligent recommendation on which driver to send, factoring in travel time, current workload, and driver score.</p>
                </div>
              </div>

              <!-- Job Status -->
              <div class="flex items-start space-x-3">
                <div class="w-8 h-8 rounded-lg bg-yellow-600 bg-opacity-20 flex items-center justify-center flex-shrink-0">
                  <i class="fa-solid fa-briefcase text-yellow-400 text-sm"></i>
                </div>
                <div>
                  <div class="text-white text-sm font-semibold">Job Stage Tracking</div>
                  <p class="text-gray-400 text-xs mt-0.5">Automatically detects job stages: En Route, Arrived, Testing, and Completed based on proximity and speed. Shows milestone progress bars per vehicle.</p>
                </div>
              </div>

              <!-- ETA & Late Alerts -->
              <div class="flex items-start space-x-3">
                <div class="w-8 h-8 rounded-lg bg-red-600 bg-opacity-20 flex items-center justify-center flex-shrink-0">
                  <i class="fa-solid fa-clock text-red-400 text-sm"></i>
                </div>
                <div>
                  <div class="text-white text-sm font-semibold">ETA & Late Alerts</div>
                  <p class="text-gray-400 text-xs mt-0.5">Calculates estimated arrival times for each driver's current job. Red alerts appear when a driver is running late, showing how many minutes behind schedule.</p>
                </div>
              </div>

              <!-- Day Path / Trails -->
              <div class="flex items-start space-x-3">
                <div class="w-8 h-8 rounded-lg bg-indigo-600 bg-opacity-20 flex items-center justify-center flex-shrink-0">
                  <i class="fa-solid fa-route text-indigo-400 text-sm"></i>
                </div>
                <div>
                  <div class="text-white text-sm font-semibold">Trail History</div>
                  <p class="text-gray-400 text-xs mt-0.5">Toggle "Show Trails" to see each vehicle's recent path drawn on the map. Select a vehicle to view its full day path and future job routes.</p>
                </div>
              </div>

              <!-- Vehicle Detail Card -->
              <div class="flex items-start space-x-3">
                <div class="w-8 h-8 rounded-lg bg-cyan-600 bg-opacity-20 flex items-center justify-center flex-shrink-0">
                  <i class="fa-solid fa-id-card text-cyan-400 text-sm"></i>
                </div>
                <div>
                  <div class="text-white text-sm font-semibold">Vehicle Detail Card</div>
                  <p class="text-gray-400 text-xs mt-0.5">Click a vehicle to open its detail card showing current job, next job, ETA, driver score, day stats (jobs completed, distance driven, drive time), and upcoming schedule.</p>
                </div>
              </div>

              <!-- Driver Scores -->
              <div class="flex items-start space-x-3">
                <div class="w-8 h-8 rounded-lg bg-orange-600 bg-opacity-20 flex items-center justify-center flex-shrink-0">
                  <i class="fa-solid fa-star text-orange-400 text-sm"></i>
                </div>
                <div>
                  <div class="text-white text-sm font-semibold">Driver Scores</div>
                  <p class="text-gray-400 text-xs mt-0.5">Each driver has a safety/performance score based on events like harsh braking, speeding, and acceleration. Scores are visible in search results and vehicle cards.</p>
                </div>
              </div>

              <!-- Future Routes -->
              <div class="flex items-start space-x-3">
                <div class="w-8 h-8 rounded-lg bg-gray-600 bg-opacity-20 flex items-center justify-center flex-shrink-0">
                  <i class="fa-solid fa-map-location-dot text-gray-400 text-sm"></i>
                </div>
                <div>
                  <div class="text-white text-sm font-semibold">Future Route Preview</div>
                  <p class="text-gray-400 text-xs mt-0.5">When a vehicle is selected, dashed route lines show the planned path to upcoming jobs, so you can see the full schedule on the map.</p>
                </div>
              </div>

              <!-- Job Management -->
              <div class="flex items-start space-x-3">
                <div class="w-8 h-8 rounded-lg bg-teal-600 bg-opacity-20 flex items-center justify-center flex-shrink-0">
                  <i class="fa-solid fa-list-check text-teal-400 text-sm"></i>
                </div>
                <div>
                  <div class="text-white text-sm font-semibold">Job Management</div>
                  <p class="text-gray-400 text-xs mt-0.5">View and manage jobs from the vehicle detail card. See today's full job list, mark jobs as complete or cancelled, and track remaining work.</p>
                </div>
              </div>
            </div>

            <!-- Footer -->
            <div class="px-6 py-3 border-t border-gray-700 bg-gray-800 bg-opacity-50 flex-shrink-0">
              <p class="text-gray-500 text-xs">Use the search bar at the top of the map to find locations and dispatch drivers.</p>
            </div>
          </div>
        </div>

        <!-- Setup Help Modal -->
        <div v-if="showSetupHelp" class="absolute inset-0 z-30 flex items-center justify-center bg-black bg-opacity-60" @click.self="showSetupHelp = false">
          <div class="bg-gray-900 border border-gray-700 rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
            <!-- Header -->
            <div class="px-6 py-4 border-b border-gray-700 flex items-center justify-between">
              <div>
                <h3 class="text-white header-bold text-lg">Vehicle Tracker Setup</h3>
                <p class="text-xs text-gray-400 mt-0.5">Get a new vehicle online in 4 steps</p>
              </div>
              <button @click="showSetupHelp = false" class="text-gray-500 hover:text-white transition-colors">
                <i class="fa-solid fa-xmark text-lg"></i>
              </button>
            </div>

            <!-- Steps -->
            <div class="px-6 py-5 space-y-5">
              <!-- Step 1 -->
              <div class="flex items-start space-x-4">
                <div class="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">1</div>
                <div>
                  <div class="text-white text-sm font-semibold">Install the GPS device in the vehicle</div>
                  <p class="text-gray-400 text-xs mt-1">Mount and wire the GPS tracking unit into the vehicle. Ensure it has a clear view of the sky for satellite signal and a stable power connection.</p>
                </div>
              </div>

              <!-- Step 2 -->
              <div class="flex items-start space-x-4">
                <div class="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">2</div>
                <div>
                  <div class="text-white text-sm font-semibold">Configure the device server IP</div>
                  <p class="text-gray-400 text-xs mt-1">In the GPS device settings, set the server address to point to your tracking server. This allows the device to report its position in real time.</p>
                  <div class="mt-2 bg-gray-800 rounded-lg px-3 py-2 flex items-center justify-between">
                    <code class="text-primary text-sm font-mono">34.61.85.42:8082</code>
                    <button @click.stop="copyIp" class="text-gray-400 hover:text-white transition-colors text-xs ml-3">
                      <i :class="ipCopied ? 'fa-solid fa-check text-green-400' : 'fa-regular fa-copy'"></i>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Step 3 -->
              <div class="flex items-start space-x-4">
                <div class="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">3</div>
                <div>
                  <div class="text-white text-sm font-semibold">Add the device in Traccar</div>
                  <p class="text-gray-400 text-xs mt-1">Log into the Traccar dashboard and add a new device using the unit's IMEI as the identifier. The Cloud Function will automatically detect it and create the vehicle record.</p>
                  <div class="mt-2 bg-gray-800 rounded-lg px-3 py-2 flex items-center justify-between">
                    <div>
                      <div class="text-gray-500 text-xs">Traccar Dashboard</div>
                      <code class="text-primary text-sm font-mono">http://34.61.85.42:8082</code>
                    </div>
                    <button @click.stop="copyTraccarUrl" class="text-gray-400 hover:text-white transition-colors text-xs ml-3">
                      <i :class="traccarUrlCopied ? 'fa-solid fa-check text-green-400' : 'fa-regular fa-copy'"></i>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Step 4 -->
              <div class="flex items-start space-x-4">
                <div class="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">4</div>
                <div>
                  <div class="text-white text-sm font-semibold">Assign the user to the vehicle in Admin</div>
                  <p class="text-gray-400 text-xs mt-1">Go to <span class="text-primary font-medium">Settings</span> and assign the driver to the vehicle. Once linked, the vehicle will appear on this map with live position updates.</p>
                </div>
              </div>
            </div>

            <!-- Footer -->
            <div class="px-6 py-4 border-t border-gray-700 bg-gray-800 bg-opacity-50">
              <p class="text-gray-500 text-xs">After completing all steps, the vehicle marker will appear on the map automatically once the device begins transmitting.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import MapboxGeocoder from "@mapbox/mapbox-gl-geocoder";
import "@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css";
import { getAuth } from "firebase/auth";
import axios from "axios";
import NavMenu from "@/components/headers/navMenu";
import VehicleJobCard from "@/components/cards/vehicleJobCard";
import {
  pollVehicles,
  getTodaysJobsByContractor,
  getDriverScores,
  getVehiclePositionHistory,
  getTeamMembers,
  getTeamLeaders
} from "../firestore/utils";

const MAPBOX_TOKEN = "pk.eyJ1IjoicHJlbWFya2V0aG9tZXMiLCJhIjoiY21qamRkb3BsMGU2NzNlb3k4MWs2Y3NtaCJ9.JxBalubMXzSxJWsc2eTWrw";

// Distinct marker colours used to tell testers apart in the team leader view
const TESTER_COLORS = [
  "#3b82f6", "#f97316", "#a855f7", "#14b8a6", "#ec4899",
  "#eab308", "#22c55e", "#6366f1", "#ef4444", "#06b6d4",
  "#84cc16", "#f43f5e",
];

// Extract lat/lng from a job, handling various field structures
function getJobCoords(job) {
  if (!job) return null;
  // Try address.latitude / address.longitude (Google Places format)
  if (job.address && job.address.latitude && job.address.longitude) {
    return { lat: job.address.latitude, lng: job.address.longitude };
  }
  // Try geopoint (Firebase GeoPoint)
  if (job.geopoint) {
    const gp = job.geopoint;
    if (gp.latitude && gp.longitude) return { lat: gp.latitude, lng: gp.longitude };
    if (gp._lat && gp._long) return { lat: gp._lat, lng: gp._long };
  }
  // Try top-level lat/lng
  if (job.latitude && job.longitude) return { lat: job.latitude, lng: job.longitude };
  if (job.lat && job.lng) return { lat: job.lat, lng: job.lng };
  return null;
}

// Format a job address for display
function formatJobAddress(job) {
  if (!job) return '';
  if (job.formattedAddress) return job.formattedAddress;
  if (job.address && typeof job.address === 'object') {
    const a = job.address;
    const parts = [a.street_number, a.route, a.locality, a.administrative_area_level_1, a.postal_code].filter(Boolean);
    if (parts.length > 0) return parts.join(' ');
  }
  if (typeof job.address === 'string') return job.address;
  return '';
}

// Convert Firestore Timestamp to JS Date
function toDate(val) {
  if (!val) return null;
  if (val.toDate) return val.toDate(); // Firestore Timestamp
  if (val.seconds) return new Date(val.seconds * 1000); // Timestamp-like
  return new Date(val);
}

// Distance between two points in meters (Haversine)
function haversineDistance(lat1, lon1, lat2, lon2) {
  const R = 6371000;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export default {
  components: { NavMenu, VehicleJobCard },

  data() {
    return {
      map: null,
      markers: {},
      jobMarkers: {},
      trailSources: {},
      routeSources: {},
      vehicles: [],
      vehicleJobs: {},       // vehicleId -> { current, next, all }
      vehicleScores: {},     // vehicleId -> driverScore
      vehicleEtas: {},       // vehicleId -> { eta, isLate, lateBy }
      selectedVehicleId: null,
      unsubscribe: null,
      etaTimers: {},          // vehicleId -> last ETA fetch timestamp
      etaThrottleMs: 60000,   // 1 min between ETA calls per vehicle
      // Location search
      geocoder: null,
      searchLocation: null,    // { name, lat, lng }
      searchResults: [],       // [{ vehicleId, name, duration, distance, ... }]
      searchLoading: false,
      searchMarker: null,
      alertsCollapsed: false,
      showSetupHelp: false,
      showFeatures: false,
      showTrails: false,
      ipCopied: false,
      traccarUrlCopied: false,
      // AI Dispatch Advisor
      dispatchLoading: false,
      dispatchRecommendation: null,
      markerInners: {},      // vehicleId -> inner DOM element for slide animation
      markerPositions: {},   // vehicleId -> { lng, lat } last known position
      // Day view state
      selectedVehicleDayPath: [],       // position history for selected vehicle
      selectedVehicleFutureRoutes: [],  // future route geometries
      dayPathLoading: false,
      // Job focus mode
      focusedJob: null,                 // job object being focused
      focusedRouteDrawn: false,         // whether the focus route is currently drawn
      // Timeline scrubber — dial is always visible; historyMode controls whether ghost markers are active
      historyMode: false,
      historyDate: '',
      historyLoading: false,
      historyPlaying: false,
      historyPlayTimer: null,
      historyTime: new Date(),
      historyStartTime: new Date(Date.now() - 18 * 3600000),
      historyEndTime: new Date(),
      historySliderValue: 1000,
      positionHistoryCache: {},
      ghostMarkers: {},
      historyPathDrawn: false,
      // Toolbar state
      searchOpen: false,
      dialDragging: false,
      dismissedAlertIds: [],
      sidebarOpen: true,
      // Team leader
      teamMemberIds: [],
      teamMembers: [],
      allTeamLeaders: [],
    };
  },

  computed: {
    currentUser() {
      return this.$store.state.currentUser;
    },
    isAdmin() {
      return this.currentUser && this.currentUser.role === 'admin';
    },
    isTeamLeader() {
      return this.currentUser && this.currentUser.isTeamLeader === true && this.currentUser.role !== 'admin';
    },
    onlineVehicles() {
      let filtered = this.vehicles.filter(v => v.online && !v.hiddenFromTracker);
      if (this.isTeamLeader && this.teamMemberIds.length > 0) {
        filtered = filtered.filter(v => this.teamMemberIds.includes(v.assignedUserId));
      }
      return filtered;
    },
    vehiclesWithJobs() {
      let filtered = this.vehicles.filter(v => !v.hiddenFromTracker);
      // Team leaders only see their team members' vehicles
      if (this.isTeamLeader && this.teamMemberIds.length > 0) {
        filtered = filtered.filter(v => this.teamMemberIds.includes(v.assignedUserId));
      }
      return filtered.map(v => {
        const jobs = this.vehicleJobs[v.id] || {};
        const etaInfo = this.vehicleEtas[v.id] || {};
        const score = this.vehicleScores[v.id] || null;
        const currentJob = jobs.current || null;
        const jobStage = this.detectJobStage(v, currentJob);
        const onSite = jobStage === 'arrived' || jobStage === 'testing';
        // No current job = no late alert (clears stale ETA data for completed/cancelled jobs)
        const suppressLate = !currentJob || onSite;
        return {
          ...v,
          _currentJob: currentJob,
          _nextJob: jobs.next || null,
          _allJobs: jobs.all || [],
          _jobStage: jobStage,
          _eta: suppressLate ? null : (etaInfo.eta || null),
          _isLate: suppressLate ? false : (etaInfo.isLate || false),
          _lateBy: suppressLate ? 0 : (etaInfo.lateBy || 0),
          _driverScore: score
        };
      });
    },
    selectedVehicle() {
      if (!this.selectedVehicleId) return null;
      return this.vehiclesWithJobs.find(v => v.id === this.selectedVehicleId) || null;
    },
    lateVehicles() {
      return this.vehiclesWithJobs.filter(v => v._isLate);
    },
    visibleLateVehicles() {
      return this.lateVehicles.filter(v => !this.dismissedAlertIds.includes(v.id));
    },
    searchResultsOnline() {
      return this.searchResults
        .filter(r => r.online)
        .sort((a, b) => (a.duration ?? Infinity) - (b.duration ?? Infinity));
    },
    selectedVehicleDayStats() {
      if (!this.selectedVehicle) return null;
      const allJobs = this.selectedVehicle._allJobs || [];
      const jobsCompleted = allJobs.filter(j => j.status === 'COMPLETED').length;
      const jobsCancelled = allJobs.filter(j => j.status === 'CANCELLED').length;
      const jobsRemaining = allJobs.length - jobsCompleted - jobsCancelled;
      const path = this.selectedVehicleDayPath;
      let distanceKm = 0;
      let driveTimeMin = 0;
      if (path.length > 1) {
        for (let i = 1; i < path.length; i++) {
          const prev = path[i - 1];
          const curr = path[i];
          const d = haversineDistance(prev.latitude, prev.longitude, curr.latitude, curr.longitude);
          distanceKm += d / 1000;
          // Sum time between points where speed > 2 km/h
          const prevTs = toDate(prev.timestamp);
          const currTs = toDate(curr.timestamp);
          if (prevTs && currTs && (curr.speed > 2 || prev.speed > 2)) {
            driveTimeMin += (currTs - prevTs) / 60000;
          }
        }
      }
      return {
        driveTimeMin: Math.round(driveTimeMin),
        distanceKm: Math.round(distanceKm),
        jobsCompleted,
        jobsRemaining,
        totalJobs: allJobs.length
      };
    },
    selectedVehicleFutureJobs() {
      if (!this.selectedVehicle) return [];
      const allJobs = this.selectedVehicle._allJobs || [];
      return allJobs.filter(j => j.status !== 'COMPLETED' && j.status !== 'CANCELLED').slice(1);
    },
    historyTimeFormatted() {
      if (!this.historyTime) return '';
      return this.$moment(this.historyTime).format('h:mm A');
    },
    historyHourTicks() {
      if (!this.historyStartTime || !this.historyEndTime) return [];
      const ticks = [];
      const start = new Date(this.historyStartTime);
      start.setMinutes(0, 0, 0);
      start.setHours(start.getHours() + 1);
      while (start < this.historyEndTime) {
        const pct = (start - this.historyStartTime) / (this.historyEndTime - this.historyStartTime) * 100;
        ticks.push({ time: new Date(start), label: this.$moment(start).format('h A'), pct });
        start.setHours(start.getHours() + 1);
      }
      return ticks;
    }
  },

  watch: {
    selectedVehicleId(newId, oldId) {
      // Exit job focus when switching vehicles
      if (oldId && this.focusedJob) this.exitJobFocus();
      // Skip day path/future routes in history mode
      if (this.historyMode) return;
      // Clean up previous day path
      if (oldId) {
        this.removeDayPath();
        this.removeFutureRoutes();
        this.selectedVehicleDayPath = [];
        this.selectedVehicleFutureRoutes = [];
      }
      if (newId) {
        this.fetchAndDrawDayPath(newId);
        this.$nextTick(() => this.drawFutureRoutes());
      }
    }
  },

  methods: {
    // ── Map Init ──
    initMap() {
      mapboxgl.accessToken = MAPBOX_TOKEN;
      this.map = new mapboxgl.Map({
        container: this.$refs.mapContainer,
        style: "mapbox://styles/mapbox/dark-v11",
        center: [153.2278, -27.7305],
        zoom: 10,
      });
      this.map.addControl(new mapboxgl.NavigationControl(), "bottom-right");

      // Mapbox Geocoder
      this.geocoder = new MapboxGeocoder({
        accessToken: MAPBOX_TOKEN,
        mapboxgl: mapboxgl,
        marker: false, // We add our own pin marker
        placeholder: 'Search location to compare driver ETAs...',
        countries: 'au',
        types: 'place,locality,neighborhood,address,poi',
      });
      this.$refs.geocoderContainer.appendChild(this.geocoder.onAdd(this.map));

      this.geocoder.on('result', (e) => {
        const feature = e.result;
        const [lng, lat] = feature.center;
        this.searchLocation = {
          name: feature.text,
          fullName: feature.place_name,
          lat,
          lng
        };

        // Drop a pin on the map
        if (this.searchMarker) this.searchMarker.remove();
        const el = document.createElement('div');
        el.style.cssText = `
          width: 20px; height: 20px; border-radius: 50%;
          background: #6366f1; border: 3px solid white;
          box-shadow: 0 0 12px #6366f188;
        `;
        this.searchMarker = new mapboxgl.Marker({ element: el })
          .setLngLat([lng, lat])
          .addTo(this.map);

        this.calculateDriverDistances(lat, lng);
      });

      this.geocoder.on('clear', () => {
        this.clearSearch();
      });

      this.map.on("load", () => {
        this.renderAllVehicles();
      });
    },

    // ── Render vehicles on map ──
    renderAllVehicles() {
      this.vehiclesWithJobs.forEach(v => {
        this.renderVehicleMarker(v);
        this.renderJobMarker(v);
      });
    },

    renderVehicleMarker(v) {
      if (!v.latitude || !v.longitude) return;

      const targetLng = v.longitude;
      const targetLat = v.latitude;

      if (this.markers[v.id]) {
        // Update marker style
        const inner = this.markerInners[v.id];
        const color = this.vehicleColor(v);
        inner.querySelector('.marker-dot').style.borderColor = color;
        inner.querySelector('.marker-dot').style.boxShadow = `0 0 12px ${color}88`;
        inner.querySelector('.marker-dot').innerHTML = this.markerIcon(v._jobStage);
        const pulse = inner.querySelector('.marker-pulse');
        if (pulse) pulse.style.borderColor = color;

        // Slide the inner container via CSS pixel offset, then snap
        // Geographic position is set immediately so zoom/pan stay correct
        const prev = this.markerPositions[v.id];
        if (prev && this.map) {
          const oldPx = this.map.project([prev.lng, prev.lat]);
          const newPx = this.map.project([targetLng, targetLat]);
          const dx = oldPx.x - newPx.x;
          const dy = oldPx.y - newPx.y;
          // Only animate if moved more than a couple pixels
          if (Math.abs(dx) > 2 || Math.abs(dy) > 2) {
            inner.style.transition = 'none';
            inner.style.transform = `translate(${dx}px, ${dy}px)`;
            requestAnimationFrame(() => {
              inner.style.transition = 'transform 1.5s cubic-bezier(0.22, 1, 0.36, 1)';
              inner.style.transform = 'translate(0, 0)';
            });
          }
        }

        this.markers[v.id].setLngLat([targetLng, targetLat]);
        this.markerPositions[v.id] = { lng: targetLng, lat: targetLat };
      } else {
        // Outer wrapper — Mapbox controls its CSS transform for positioning.
        // Do NOT set position or transform on this element.
        const wrapper = document.createElement("div");
        wrapper.style.cssText = `width: 44px; height: 44px; cursor: pointer;`;

        // Inner container — we animate this for smooth slides
        const inner = document.createElement("div");
        inner.className = 'marker-inner';
        inner.style.cssText = `position: absolute; top: 0; left: 0; width: 44px; height: 44px;`;

        const color = this.vehicleColor(v);

        // Pulse ring
        const pulse = document.createElement("div");
        pulse.className = 'marker-pulse';
        pulse.style.cssText = `
          position: absolute; top: 0; left: 0; width: 44px; height: 44px;
          border-radius: 50%; border: 2px solid ${color};
          animation: marker-pulse-ring 2s ease-out infinite;
          pointer-events: none;
        `;
        inner.appendChild(pulse);

        // Main dot
        const el = document.createElement("div");
        el.className = 'marker-dot';
        el.style.cssText = `
          position: absolute; top: 4px; left: 4px;
          width: 36px; height: 36px; border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          font-size: 16px;
          border: 3px solid ${color};
          background: #1a1a2e; color: white;
          box-shadow: 0 0 12px ${color}88;
          transition: border-color 0.4s ease, box-shadow 0.4s ease;
        `;
        el.innerHTML = this.markerIcon(v._jobStage);
        inner.appendChild(el);

        wrapper.appendChild(inner);
        wrapper.addEventListener("click", () => this.centerOnVehicle(v));

        const marker = new mapboxgl.Marker({ element: wrapper, anchor: 'center' })
          .setLngLat([targetLng, targetLat])
          .addTo(this.map);
        this.markers[v.id] = marker;
        this.markerInners[v.id] = inner;
        this.markerPositions[v.id] = { lng: targetLng, lat: targetLat };
      }

      this.updateTrailLine(v);
    },

    renderJobMarker(v) {
      const job = v._currentJob;
      const coords = getJobCoords(job);
      if (!coords) {
        if (this.jobMarkers[v.id]) {
          this.jobMarkers[v.id].remove();
          delete this.jobMarkers[v.id];
        }
        return;
      }

      if (this.jobMarkers[v.id]) {
        this.jobMarkers[v.id].setLngLat([coords.lng, coords.lat]);
      } else {
        const el = document.createElement("div");
        const color = this.vehicleColor(v);
        el.style.cssText = `
          width: 14px; height: 14px; border-radius: 50%;
          border: 2px solid white;
          background: ${color};
          box-shadow: 0 0 8px ${color}88;
          cursor: pointer;
        `;

        const addr = formatJobAddress(job);
        const popup = new mapboxgl.Popup({ offset: 12 }).setHTML(
          `<div style="font-size:12px;padding:4px;">
            <strong>${job.company || job.projectName || job.projectId || 'Job'}</strong><br/>
            <span style="color:#888;">${addr}</span>
          </div>`
        );

        const marker = new mapboxgl.Marker({ element: el })
          .setLngLat([coords.lng, coords.lat])
          .setPopup(popup)
          .addTo(this.map);
        this.jobMarkers[v.id] = marker;
      }
    },

    toggleTrails() {
      this.showTrails = !this.showTrails;
      if (this.showTrails) {
        // Draw trails for all vehicles
        this.vehiclesWithJobs.forEach(v => this.updateTrailLine(v));
      } else {
        // Remove all trail layers
        this.removeAllTrails();
      }
    },

    removeAllTrails() {
      if (!this.map) return;
      for (const id of Object.keys(this.trailSources)) {
        const sourceId = `trail-${id}`;
        const layerId = `trail-layer-${id}`;
        if (this.map.getLayer(layerId)) this.map.removeLayer(layerId);
        if (this.map.getSource(sourceId)) this.map.removeSource(sourceId);
      }
      this.trailSources = {};
    },

    updateTrailLine(v) {
      if (!this.map || !this.map.isStyleLoaded()) return;
      if (!this.showTrails) return;
      if (!v.latitude || !v.longitude) return;

      const sourceId = `trail-${v.id}`;
      const layerId = `trail-layer-${v.id}`;
      const coords = [[v.longitude, v.latitude]];

      if (v.recentPositions && v.recentPositions.length > 0) {
        v.recentPositions.forEach(p => {
          if (p.longitude && p.latitude) {
            coords.unshift([p.longitude, p.latitude]);
          }
        });
      }

      if (coords.length < 2) return;

      const data = {
        type: "Feature",
        geometry: { type: "LineString", coordinates: coords }
      };

      if (this.map.getSource(sourceId)) {
        this.map.getSource(sourceId).setData(data);
      } else {
        this.map.addSource(sourceId, { type: "geojson", data });
        this.map.addLayer({
          id: layerId,
          type: "line",
          source: sourceId,
          paint: {
            "line-color": this.vehicleColor(v),
            "line-width": 3,
            "line-opacity": 0.5,
            "line-dasharray": [2, 2],
          },
        });
        this.trailSources[v.id] = true;
      }
    },

    // ── Route visualization (Mapbox Directions) ──
    // Draws route to next job. Green = on time, Red = late.
    async fetchAndDrawRoute(vehicle) {
      const job = vehicle._currentJob;
      // Skip completed/cancelled jobs — stale data from refresh lag
      if (!job || job.status === 'COMPLETED' || job.status === 'CANCELLED') {
        this.removeRoute(vehicle.id);
        this.$set(this.vehicleEtas, vehicle.id, { eta: null, isLate: false, lateBy: 0 });
        return;
      }
      const jobCoords = getJobCoords(job);
      if (!jobCoords || !vehicle.latitude || !vehicle.longitude) {
        this.removeRoute(vehicle.id);
        return;
      }

      const url = `https://api.mapbox.com/directions/v5/mapbox/driving/${vehicle.longitude},${vehicle.latitude};${jobCoords.lng},${jobCoords.lat}?geometries=geojson&access_token=${MAPBOX_TOKEN}`;

      try {
        const response = await fetch(url);
        const data = await response.json();
        if (!data.routes || data.routes.length === 0) return;

        const route = data.routes[0];
        const etaMin = Math.round(route.duration / 60);

        // Check if late: compare arrival time with scheduled job time
        const jobDate = toDate(job.date);
        let isLate = false;
        let lateBy = 0;
        if (jobDate) {
          const arrivalTime = new Date(Date.now() + route.duration * 1000);
          if (arrivalTime > jobDate) {
            isLate = true;
            lateBy = Math.round((arrivalTime - jobDate) / 60000);
          }
        }

        this.$set(this.vehicleEtas, vehicle.id, { eta: etaMin, isLate, lateBy });
        this.etaTimers[vehicle.id] = Date.now();

        // Route color: green if on time, red if late
        const routeColor = isLate ? '#ef4444' : '#22c55e';

        const sourceId = `route-${vehicle.id}`;
        const layerId = `route-layer-${vehicle.id}`;
        const geojson = {
          type: "Feature",
          geometry: route.geometry
        };

        if (this.map.getSource(sourceId)) {
          this.map.getSource(sourceId).setData(geojson);
          // Update color on existing layer
          if (this.map.getLayer(layerId)) {
            this.map.setPaintProperty(layerId, 'line-color', routeColor);
          }
        } else {
          this.map.addSource(sourceId, { type: "geojson", data: geojson });
          this.map.addLayer({
            id: layerId,
            type: "line",
            source: sourceId,
            paint: {
              "line-color": routeColor,
              "line-width": 4,
              "line-opacity": 0.8
            }
          });
          this.routeSources[vehicle.id] = true;
        }
      } catch (e) {
        console.error("Directions API error:", e);
      }
    },

    removeRoute(vehicleId) {
      if (!this.map) return;
      const sourceId = `route-${vehicleId}`;
      const layerId = `route-layer-${vehicleId}`;
      if (this.map.getLayer(layerId)) this.map.removeLayer(layerId);
      if (this.map.getSource(sourceId)) this.map.removeSource(sourceId);
      delete this.routeSources[vehicleId];
    },

    // ── Job Stage Detection ──
    detectJobStage(vehicle, currentJob) {
      // Manual en-route takes priority over vehicle online status
      if (currentJob && currentJob.enRoute) return 'en_route';

      // Check proximity using last known position BEFORE online check
      // Testers often go offline on-site (no signal, phone locked, etc.)
      const jobCoords = getJobCoords(currentJob);
      if (jobCoords && vehicle.latitude && vehicle.longitude) {
        const dist = haversineDistance(
          vehicle.latitude, vehicle.longitude,
          jobCoords.lat, jobCoords.lng
        );
        if (dist < 200) return 'testing';
        if (dist < 1000) return 'arrived';
      }

      if (!vehicle.online) return 'offline';
      if (!jobCoords) {
        const speed = vehicle.speed || 0;
        return speed > 5 ? 'moving' : 'idle';
      }
      const speed = vehicle.speed || 0;
      return speed > 5 ? 'moving' : 'idle';
    },

    // ── ETA throttling ──
    shouldFetchEta(vehicleId) {
      const last = this.etaTimers[vehicleId] || 0;
      return (Date.now() - last) > this.etaThrottleMs;
    },

    // ── Data Loading ──
    async loadJobsForVehicle(vehicle) {
      if (!vehicle.assignedUserId) return;
      try {
        const jobs = await getTodaysJobsByContractor(vehicle.assignedUserId);
        console.log(`Jobs for ${vehicle.assignedUserName || vehicle.id}:`, jobs.length);
        const pending = jobs.filter(j => j.status !== 'COMPLETED' && j.status !== 'CANCELLED');
        const current = pending[0] || null;
        const next = pending[1] || null;
        this.$set(this.vehicleJobs, vehicle.id, { current, next, all: jobs });

        if (!current) {
          // No pending job — clear stale ETA/late data and remove route line
          this.$set(this.vehicleEtas, vehicle.id, { eta: null, isLate: false, lateBy: 0 });
          this.removeRoute(vehicle.id);
        } else if (getJobCoords(current) && vehicle.latitude && vehicle.longitude) {
          // Immediately fetch route if vehicle has a job with coordinates
          const vWithJob = this.vehiclesWithJobs.find(vj => vj.id === vehicle.id);
          if (vWithJob) {
            this.fetchAndDrawRoute(vWithJob);
            this.renderJobMarker(vWithJob);
          }
        }
      } catch (e) {
        console.error("Error loading jobs for vehicle:", vehicle.id, e);
      }
    },

    onJobUpdated() {
      // Refresh jobs for the selected vehicle after an inline edit
      if (!this.selectedVehicleId) return;
      const vehicle = this.vehicles.find(v => v.id === this.selectedVehicleId);
      if (vehicle) {
        this.loadJobsForVehicle(vehicle);
      }
    },

    async loadDriverScore(vehicle) {
      if (!vehicle.assignedUserId) return;
      try {
        const scores = await getDriverScores(vehicle.assignedUserId);
        if (scores.length > 0) {
          this.$set(this.vehicleScores, vehicle.id, scores[0]);
        }
      } catch (e) {
        console.error("Error loading driver score:", e);
      }
    },

    // ── Vehicle snapshot handler ──
    async onVehiclesUpdate(newVehicles) {
      if (this.historyMode) {
        this.vehicles = newVehicles;
        return;
      }
      const prevIds = new Set(this.vehicles.map(v => v.id));
      this.vehicles = newVehicles;

      for (const v of newVehicles) {
        // Load jobs if we haven't yet or if it's a new vehicle
        if (!this.vehicleJobs[v.id] || !prevIds.has(v.id)) {
          this.loadJobsForVehicle(v);
          this.loadDriverScore(v);
        }

        // Update map markers
        if (this.map && this.map.isStyleLoaded()) {
          const vWithJob = this.vehiclesWithJobs.find(vj => vj.id === v.id);
          if (vWithJob) {
            this.renderVehicleMarker(vWithJob);
            this.renderJobMarker(vWithJob);

            // If on-site (arrived/testing), clear any stale late data
            const onSite = vWithJob._jobStage === 'arrived' || vWithJob._jobStage === 'testing';
            if (onSite) {
              if (this.vehicleEtas[v.id] && this.vehicleEtas[v.id].isLate) {
                this.$set(this.vehicleEtas, v.id, { eta: null, isLate: false, lateBy: 0 });
              }
              this.removeRoute(v.id);
            } else if (vWithJob._currentJob && getJobCoords(vWithJob._currentJob)) {
              // Fetch route (throttled)
              if (this.shouldFetchEta(v.id)) {
                this.fetchAndDrawRoute(vWithJob);
              }
            } else if (this.vehicleEtas[v.id] && this.vehicleEtas[v.id].isLate) {
              // No current job but stale late data — clear it
              this.$set(this.vehicleEtas, v.id, { eta: null, isLate: false, lateBy: 0 });
              this.removeRoute(v.id);
            }
          }
        }
      }

      // Remove markers for vehicles no longer in list
      const currentIds = new Set(newVehicles.map(v => v.id));
      for (const id of prevIds) {
        if (!currentIds.has(id)) {
          if (this.markers[id]) { this.markers[id].remove(); delete this.markers[id]; }
          delete this.markerInners[id];
          delete this.markerPositions[id];
          if (this.jobMarkers[id]) { this.jobMarkers[id].remove(); delete this.jobMarkers[id]; }
          this.removeRoute(id);
        }
      }
    },

    // ── Location Search ──

    async calculateDriverDistances(destLat, destLng) {
      this.searchLoading = true;
      const onlineWithPosition = this.vehiclesWithJobs.filter(
        v => v.latitude && v.longitude
      );

      // Build initial results (show immediately while loading)
      this.searchResults = onlineWithPosition.map(v => {
        const etaInfo = this.vehicleEtas[v.id] || {};
        const currentJob = v._currentJob || null;
        const jobName = currentJob ? (currentJob.company || currentJob.projectName || currentJob.projectId || 'Job') : null;
        const jobStage = v._jobStage;
        const currentJobEta = etaInfo.eta || null;
        const isLate = etaInfo.isLate || false;
        const lateBy = etaInfo.lateBy || 0;
        const score = v._driverScore ? v._driverScore.score : null;

        return {
          vehicleId: v.id,
          vehicle: v,
          name: v.assignedUserName || v.name || v.id,
          online: v.online,
          currentLocation: this.describeLocation(v),
          duration: null,
          distance: null,
          durationText: '',
          distanceText: '',
          loading: true,
          // Enriched fields
          jobName,
          jobStage,
          currentJobEta,
          isLate,
          lateBy,
          driverScore: score,
          availabilityText: currentJob ? (currentJobEta ? `Available in ~${currentJobEta} min` : 'On a job') : 'Available now',
          availabilityMin: currentJob ? (currentJobEta || 999) : 0
        };
      });

      // Fetch directions for each vehicle in parallel
      const promises = onlineWithPosition.map(async (v, idx) => {
        try {
          const url = `https://api.mapbox.com/directions/v5/mapbox/driving/${v.longitude},${v.latitude};${destLng},${destLat}?access_token=${MAPBOX_TOKEN}`;
          const response = await fetch(url);
          const data = await response.json();

          if (data.routes && data.routes.length > 0) {
            const route = data.routes[0];
            const durationSec = route.duration;
            const distanceM = route.distance;
            const travelMin = Math.round(durationSec / 60);
            const result = this.searchResults[idx];
            // Total availability = current job ETA remaining + travel to searched location
            const totalAvailMin = result.jobName
              ? (result.currentJobEta || 0) + travelMin
              : travelMin;
            const availText = result.jobName
              ? `Available in ~${totalAvailMin} min`
              : 'Available now';

            this.$set(this.searchResults, idx, {
              ...result,
              duration: durationSec,
              distance: distanceM,
              durationText: this.formatDuration(durationSec),
              distanceText: this.formatDistance(distanceM),
              loading: false,
              availabilityText: availText,
              availabilityMin: totalAvailMin
            });
          } else {
            this.$set(this.searchResults, idx, {
              ...this.searchResults[idx],
              loading: false,
              durationText: 'N/A',
              distanceText: ''
            });
          }
        } catch (e) {
          this.$set(this.searchResults, idx, {
            ...this.searchResults[idx],
            loading: false,
            durationText: 'Error',
            distanceText: ''
          });
        }
      });

      await Promise.all(promises);
      this.searchLoading = false;
    },

    clearSearch() {
      this.searchLocation = null;
      this.searchResults = [];
      this.searchLoading = false;
      this.dispatchRecommendation = null;
      if (this.searchMarker) {
        this.searchMarker.remove();
        this.searchMarker = null;
      }
    },

    async getDispatchRecommendation() {
      if (this.dispatchLoading || this.searchResultsOnline.length === 0) return;
      this.dispatchLoading = true;
      this.dispatchRecommendation = null;

      try {
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) throw new Error('Not authenticated');
        const token = await user.getIdToken();

        const drivers = this.searchResultsOnline.map(r => ({
          name: r.name,
          travelTime: r.durationText,
          distance: r.distanceText,
          jobName: r.jobName || null,
          jobStage: r.jobStage || null,
          currentJobEta: r.currentJobEta || null,
          isLate: r.isLate || false,
          lateBy: r.lateBy || 0,
          driverScore: r.driverScore,
          availabilityText: r.availabilityText
        }));

        const response = await axios.post(
          'https://us-central1-ktc-app-33f33.cloudfunctions.net/widgets/api/v1/dispatch-advisor',
          {
            searchedLocation: this.searchLocation ? this.searchLocation.fullName || this.searchLocation.name : 'Unknown',
            drivers
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        this.dispatchRecommendation = response.data.data.recommendation;
      } catch (e) {
        console.error('Dispatch advisor error:', e);
        this.dispatchRecommendation = 'Sorry, could not get a recommendation. Please try again.';
      } finally {
        this.dispatchLoading = false;
      }
    },

    describeLocation(v) {
      // Quick description from lat/lng
      if (!v.latitude) return 'Unknown';
      const speed = Math.round(v.speed || 0);
      if (speed > 2) return `Moving ${speed} km/h`;
      return 'Stationary';
    },

    formatDuration(seconds) {
      if (seconds == null) return '';
      const mins = Math.round(seconds / 60);
      if (mins < 60) return `${mins} min`;
      const hrs = Math.floor(mins / 60);
      const remMin = mins % 60;
      return `${hrs}h ${remMin}m`;
    },

    formatDistance(meters) {
      if (meters == null) return '';
      if (meters < 1000) return `${Math.round(meters)} m`;
      return `${(meters / 1000).toFixed(1)} km`;
    },

    // ── Day Path + Future Routes ──
    async fetchAndDrawDayPath(vehicleId) {
      this.dayPathLoading = true;
      try {
        const positions = await getVehiclePositionHistory(vehicleId, 18);
        this.selectedVehicleDayPath = positions;
        this.drawDayPath(positions);
      } catch (e) {
        console.error('Error fetching position history:', e);
      } finally {
        this.dayPathLoading = false;
      }
    },

    drawDayPath(positions) {
      if (!this.map || !this.map.isStyleLoaded() || positions.length < 2) return;
      const coords = positions
        .filter(p => p.longitude && p.latitude)
        .map(p => [p.longitude, p.latitude]);
      if (coords.length < 2) return;

      const sourceId = 'day-path';
      const layerId = 'day-path-layer';
      const data = {
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: coords }
      };

      if (this.map.getSource(sourceId)) {
        this.map.getSource(sourceId).setData(data);
      } else {
        this.map.addSource(sourceId, { type: 'geojson', data });
        this.map.addLayer({
          id: layerId,
          type: 'line',
          source: sourceId,
          paint: {
            'line-color': '#3b82f6',
            'line-width': 3,
            'line-opacity': 0.7,
            'line-dasharray': [3, 2]
          }
        });
      }
    },

    removeDayPath() {
      if (!this.map) return;
      if (this.map.getLayer('day-path-layer')) this.map.removeLayer('day-path-layer');
      if (this.map.getSource('day-path')) this.map.removeSource('day-path');
    },

    async drawFutureRoutes() {
      this.removeFutureRoutes();
      const v = this.selectedVehicle;
      if (!v || !v.latitude || !v.longitude) return;
      const futureJobs = this.selectedVehicleFutureJobs;
      if (futureJobs.length === 0) return;

      // Chain waypoints: vehicle -> current job -> future job 1 -> future job 2 ...
      const waypoints = [];
      // Start from vehicle current position
      let lastCoord = { lat: v.latitude, lng: v.longitude };
      // If current job has coords, route through it first
      const currentCoords = getJobCoords(v._currentJob);
      if (currentCoords) {
        lastCoord = currentCoords;
      }

      for (let i = 0; i < Math.min(futureJobs.length, 3); i++) {
        const jobCoords = getJobCoords(futureJobs[i]);
        if (!jobCoords) continue;
        waypoints.push({ from: lastCoord, to: jobCoords, index: i });
        lastCoord = jobCoords;
      }

      const routes = [];
      for (const wp of waypoints) {
        try {
          const url = `https://api.mapbox.com/directions/v5/mapbox/driving/${wp.from.lng},${wp.from.lat};${wp.to.lng},${wp.to.lat}?geometries=geojson&access_token=${MAPBOX_TOKEN}`;
          const response = await fetch(url);
          const data = await response.json();
          if (data.routes && data.routes.length > 0) {
            const sourceId = `future-route-${wp.index}`;
            const layerId = `future-route-layer-${wp.index}`;
            const geojson = {
              type: 'Feature',
              geometry: data.routes[0].geometry
            };
            if (!this.map.getSource(sourceId)) {
              this.map.addSource(sourceId, { type: 'geojson', data: geojson });
              this.map.addLayer({
                id: layerId,
                type: 'line',
                source: sourceId,
                paint: {
                  'line-color': '#94a3b8',
                  'line-width': 2.5,
                  'line-opacity': 0.5,
                  'line-dasharray': [4, 4]
                }
              });
            }
            routes.push(sourceId);
          }
        } catch (e) {
          console.error('Future route error:', e);
        }
      }
      this.selectedVehicleFutureRoutes = routes;
    },

    removeFutureRoutes() {
      if (!this.map) return;
      for (let i = 0; i < 5; i++) {
        const layerId = `future-route-layer-${i}`;
        const sourceId = `future-route-${i}`;
        if (this.map.getLayer(layerId)) this.map.removeLayer(layerId);
        if (this.map.getSource(sourceId)) this.map.removeSource(sourceId);
      }
      this.selectedVehicleFutureRoutes = [];
    },

    // ── UI Helpers ──
    centerOnVehicle(v) {
      if (this.focusedJob) this.exitJobFocus();
      this.selectedVehicleId = v.id;
      // Close sidebar on mobile so map is visible
      if (window.innerWidth < 768) this.sidebarOpen = false;
      if (this.historyMode && this.historyTime) {
        const interp = this.interpolatePosition(v.id, this.historyTime);
        if (interp && this.map) {
          this.map.flyTo({ center: [interp.lng, interp.lat], zoom: 14, duration: 1500 });
        }
        return;
      }
      if (this.map && v.latitude && v.longitude) {
        this.map.flyTo({
          center: [v.longitude, v.latitude],
          zoom: 14,
          duration: 1500,
        });
      }
    },

    // ── Job Focus Mode ──
    async onFocusJob(job) {
      // Toggle off if clicking the same job
      if (this.focusedJob && this.focusedJob.id === job.id) {
        this.exitJobFocus();
        return;
      }

      const v = this.selectedVehicle;
      if (!v) return;
      const jobCoords = getJobCoords(job);
      if (!jobCoords) return;

      this.focusedJob = job;

      // Hide all other vehicle markers, job markers, trails, routes, day path, future routes
      for (const vid of Object.keys(this.markers)) {
        if (vid !== v.id) {
          this.markers[vid].getElement().style.display = 'none';
        }
      }
      for (const vid of Object.keys(this.jobMarkers)) {
        this.jobMarkers[vid].getElement().style.display = 'none';
      }
      // Hide all existing route layers
      for (const vid of Object.keys(this.routeSources)) {
        const lid = `route-layer-${vid}`;
        if (this.map.getLayer(lid)) this.map.setLayoutProperty(lid, 'visibility', 'none');
      }
      // Hide trails
      for (const vid of Object.keys(this.trailSources)) {
        const lid = `trail-layer-${vid}`;
        if (this.map.getLayer(lid)) this.map.setLayoutProperty(lid, 'visibility', 'none');
      }
      // Hide day path + future routes
      if (this.map.getLayer('day-path-layer')) this.map.setLayoutProperty('day-path-layer', 'visibility', 'none');
      for (let i = 0; i < 5; i++) {
        const lid = `future-route-layer-${i}`;
        if (this.map.getLayer(lid)) this.map.setLayoutProperty(lid, 'visibility', 'none');
      }

      // Draw a focused job marker
      const el = document.createElement('div');
      el.style.cssText = `
        width: 18px; height: 18px; border-radius: 50%;
        border: 3px solid white; background: #22c55e;
        box-shadow: 0 0 12px #22c55e88;
      `;
      this._focusJobMarker = new mapboxgl.Marker({ element: el })
        .setLngLat([jobCoords.lng, jobCoords.lat])
        .addTo(this.map);

      // Fetch and draw route from vehicle to this job
      if (v.latitude && v.longitude) {
        try {
          const url = `https://api.mapbox.com/directions/v5/mapbox/driving/${v.longitude},${v.latitude};${jobCoords.lng},${jobCoords.lat}?geometries=geojson&access_token=${MAPBOX_TOKEN}`;
          const res = await fetch(url);
          const data = await res.json();
          if (data.routes && data.routes.length > 0) {
            const geojson = { type: 'Feature', geometry: data.routes[0].geometry };
            const sourceId = 'focus-route';
            const layerId = 'focus-route-layer';
            if (this.map.getSource(sourceId)) {
              this.map.getSource(sourceId).setData(geojson);
            } else {
              this.map.addSource(sourceId, { type: 'geojson', data: geojson });
              this.map.addLayer({
                id: layerId,
                type: 'line',
                source: sourceId,
                paint: {
                  'line-color': '#22c55e',
                  'line-width': 4,
                  'line-opacity': 0.9
                }
              });
            }
            this.focusedRouteDrawn = true;
          }
        } catch (e) {
          console.error('Focus route error:', e);
        }

        // Fit map to show both vehicle and job
        const bounds = new mapboxgl.LngLatBounds();
        bounds.extend([v.longitude, v.latitude]);
        bounds.extend([jobCoords.lng, jobCoords.lat]);
        this.map.fitBounds(bounds, { padding: 100, duration: 1200 });
      }
    },

    exitJobFocus() {
      if (!this.focusedJob) return;
      this.focusedJob = null;

      // Remove focus route
      if (this.focusedRouteDrawn) {
        if (this.map.getLayer('focus-route-layer')) this.map.removeLayer('focus-route-layer');
        if (this.map.getSource('focus-route')) this.map.removeSource('focus-route');
        this.focusedRouteDrawn = false;
      }

      // Remove focus job marker
      if (this._focusJobMarker) {
        this._focusJobMarker.remove();
        this._focusJobMarker = null;
      }

      // Restore all vehicle markers
      for (const vid of Object.keys(this.markers)) {
        this.markers[vid].getElement().style.display = '';
      }
      for (const vid of Object.keys(this.jobMarkers)) {
        this.jobMarkers[vid].getElement().style.display = '';
      }
      // Restore route layers
      for (const vid of Object.keys(this.routeSources)) {
        const lid = `route-layer-${vid}`;
        if (this.map.getLayer(lid)) this.map.setLayoutProperty(lid, 'visibility', 'visible');
      }
      // Restore trails if they were on
      if (this.showTrails) {
        for (const vid of Object.keys(this.trailSources)) {
          const lid = `trail-layer-${vid}`;
          if (this.map.getLayer(lid)) this.map.setLayoutProperty(lid, 'visibility', 'visible');
        }
      }
      // Restore day path + future routes
      if (this.map.getLayer('day-path-layer')) this.map.setLayoutProperty('day-path-layer', 'visibility', 'visible');
      for (let i = 0; i < 5; i++) {
        const lid = `future-route-layer-${i}`;
        if (this.map.getLayer(lid)) this.map.setLayoutProperty(lid, 'visibility', 'visible');
      }

      // Zoom back to see all
      if (this.selectedVehicle && this.selectedVehicle.latitude) {
        this.map.flyTo({
          center: [this.selectedVehicle.longitude, this.selectedVehicle.latitude],
          zoom: 14,
          duration: 1200
        });
      }
    },

    vehicleBgClass(v) {
      const stageMap = {
        en_route: 'bg-blue-500',
        moving: 'bg-green-500',
        arrived: 'bg-yellow-500',
        testing: 'bg-purple-500',
        completed: 'bg-green-500',
        offline: 'bg-gray-500',
        idle: 'bg-gray-400'
      };
      return stageMap[v._jobStage] || 'bg-gray-500';
    },

    // Team leaders watch a small crew, so each tester gets their own colour to
    // tell them apart on the map. Admins keep the stage colouring.
    testerColor(v) {
      const key = v.assignedUserId || v.id;
      if (!key) return '#6b7280';
      let hash = 0;
      for (let i = 0; i < key.length; i++) {
        hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
      }
      return TESTER_COLORS[hash % TESTER_COLORS.length];
    },

    vehicleColor(v) {
      if (this.isTeamLeader) return this.testerColor(v);
      return this.stageColor(v._jobStage);
    },

    stageColor(stage) {
      const map = {
        en_route: '#3b82f6',
        moving: '#22c55e',
        arrived: '#eab308',
        testing: '#a855f7',
        completed: '#22c55e',
        offline: '#6b7280',
        idle: '#9ca3af'
      };
      return map[stage] || '#6b7280';
    },

    stagePillClass(stage) {
      const map = {
        en_route: 'bg-blue-900 text-blue-300',
        moving: 'bg-green-900 text-green-300',
        arrived: 'bg-yellow-900 text-yellow-300',
        testing: 'bg-purple-900 text-purple-300',
        completed: 'bg-green-900 text-green-300',
        offline: 'bg-gray-700 text-gray-400',
        idle: 'bg-gray-700 text-gray-300'
      };
      return map[stage] || 'bg-gray-700 text-gray-300';
    },

    stageDotClass(stage) {
      const map = {
        en_route: 'bg-blue-400',
        moving: 'bg-green-400',
        arrived: 'bg-yellow-400',
        testing: 'bg-purple-400',
        completed: 'bg-green-400',
        offline: 'bg-gray-500',
        idle: 'bg-gray-400'
      };
      return map[stage] || 'bg-gray-400';
    },

    stageLabel(stage) {
      const labels = {
        en_route: 'En Route',
        moving: 'Moving',
        arrived: 'Arrived',
        testing: 'Testing',
        completed: 'Completed',
        offline: 'Offline',
        idle: 'Idle'
      };
      return labels[stage] || 'Unknown';
    },

    markerIcon(stage) {
      if (stage === 'testing') return '&#128300;';
      if (stage === 'arrived') return '&#128205;';
      if (stage === 'completed') return '&#9989;';
      if (stage === 'offline') return '&#9898;';
      return '&#128663;';
    },

    milestones(stage) {
      const stageOrder = ['en_route', 'arrived', 'testing', 'completed'];
      const idx = stageOrder.indexOf(stage);
      return [
        { label: 'En Route', color: 'bg-blue-500', active: idx >= 0 },
        { label: 'Arrived', color: 'bg-yellow-500', active: idx >= 1 },
        { label: 'Testing', color: 'bg-purple-500', active: idx >= 2 },
        { label: 'Completed', color: 'bg-green-500', active: idx >= 3 }
      ];
    },

    initials(name) {
      if (!name || name === '?') return '?';
      return name.split(' ').map(n => n[0]).join('').toUpperCase();
    },

    copyIp() {
      navigator.clipboard.writeText('34.61.85.42:8082');
      this.ipCopied = true;
      setTimeout(() => { this.ipCopied = false; }, 2000);
    },

    dismissAlert(vehicleId) {
      this.dismissedAlertIds.push(vehicleId);
    },

    // ── Toolbar ──
    toggleSearch() {
      this.searchOpen = !this.searchOpen;
      if (this.searchOpen) {
        this.$nextTick(() => {
          const input = this.$refs.geocoderContainer && this.$refs.geocoderContainer.querySelector('input');
          if (input) input.focus();
        });
      }
    },

    // ── Radio Dial Interaction ──
    onDialMouseDown(e) {
      if (this.historyLoading) return;
      this.dialDragging = true;
      this.onDialSeek(e);
      const onMove = (ev) => {
        if (!this.dialDragging) return;
        this.onDialSeek(ev);
      };
      const onUp = () => {
        this.dialDragging = false;
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        document.removeEventListener('touchmove', onMove);
        document.removeEventListener('touchend', onUp);
        // Full path redraw once drag ends
        this.drawHistoryPaths();
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
      document.addEventListener('touchmove', onMove, { passive: false });
      document.addEventListener('touchend', onUp);
    },

    onDialSeek(e) {
      const track = this.$refs.dialTrack;
      if (!track) return;
      const rect = track.getBoundingClientRect();
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      let pct = (clientX - rect.left) / rect.width;
      pct = Math.max(0, Math.min(1, pct));
      const value = Math.round(pct * 1000);
      this.onScrubberInput(value);
    },

    // ── Timeline Scrubber ──
    async enterHistoryMode() {
      if (this.historyMode) return;
      this.searchOpen = false;
      this.historyMode = true;
      this.historyDate = this.$moment().format('YYYY-MM-DD');
      await this.loadHistoryForDate(this.historyDate);
    },

    async loadHistoryForDate(dateStr) {
      this.historyLoading = true;

      const isToday = dateStr === this.$moment().format('YYYY-MM-DD');
      if (isToday) {
        this.historyEndTime = new Date();
        this.historyStartTime = new Date(Date.now() - 18 * 3600000);
      } else {
        this.historyStartTime = new Date(dateStr + 'T00:00:00');
        this.historyEndTime = new Date(dateStr + 'T23:59:59');
      }
      this.historySliderValue = 1000;
      this.historyTime = new Date(this.historyEndTime);

      // Remove existing ghost markers before reloading
      Object.values(this.ghostMarkers).forEach(m => m.remove());
      this.ghostMarkers = {};
      this.removeHistoryPath();

      // Fetch position history for all vehicles in parallel
      const promises = this.vehicles.filter(v => !v.hiddenFromTracker).map(async v => {
        try {
          const positions = isToday
            ? await getVehiclePositionHistory(v.id, 18)
            : await getVehiclePositionHistory(v.id, null, dateStr);
          this.$set(this.positionHistoryCache, v.id, positions);
        } catch (e) {
          console.error('Error fetching history for', v.id, e);
        }
      });
      await Promise.all(promises);

      // Hide live markers, job markers, routes, trails
      Object.values(this.markers).forEach(m => { m.getElement().style.display = 'none'; });
      Object.values(this.jobMarkers).forEach(m => { m.getElement().style.display = 'none'; });
      for (const vid of Object.keys(this.routeSources)) {
        const lid = `route-layer-${vid}`;
        if (this.map.getLayer(lid)) this.map.setLayoutProperty(lid, 'visibility', 'none');
      }
      for (const vid of Object.keys(this.trailSources)) {
        const lid = `trail-layer-${vid}`;
        if (this.map.getLayer(lid)) this.map.setLayoutProperty(lid, 'visibility', 'none');
      }
      if (this.map.getLayer('day-path-layer')) this.map.setLayoutProperty('day-path-layer', 'visibility', 'none');
      for (let i = 0; i < 5; i++) {
        const lid = `future-route-layer-${i}`;
        if (this.map.getLayer(lid)) this.map.setLayoutProperty(lid, 'visibility', 'none');
      }

      this.historyLoading = false;
      this.renderHistoryFrame();
    },

    async onHistoryDateChange() {
      if (!this.historyDate) return;
      // Stop playback if running
      if (this.historyPlayTimer) {
        clearInterval(this.historyPlayTimer);
        this.historyPlayTimer = null;
      }
      this.historyPlaying = false;
      await this.loadHistoryForDate(this.historyDate);
    },

    exitHistoryMode() {
      this.historyMode = false;
      this.dialDragging = false;
      // Stop play
      if (this.historyPlayTimer) {
        clearInterval(this.historyPlayTimer);
        this.historyPlayTimer = null;
      }
      this.historyPlaying = false;

      // Remove ghost markers
      Object.values(this.ghostMarkers).forEach(m => m.remove());
      this.ghostMarkers = {};

      // Remove history path
      this.removeHistoryPath();

      // Restore live markers
      Object.values(this.markers).forEach(m => { m.getElement().style.display = ''; });
      Object.values(this.jobMarkers).forEach(m => { m.getElement().style.display = ''; });
      for (const vid of Object.keys(this.routeSources)) {
        const lid = `route-layer-${vid}`;
        if (this.map.getLayer(lid)) this.map.setLayoutProperty(lid, 'visibility', 'visible');
      }
      if (this.showTrails) {
        for (const vid of Object.keys(this.trailSources)) {
          const lid = `trail-layer-${vid}`;
          if (this.map.getLayer(lid)) this.map.setLayoutProperty(lid, 'visibility', 'visible');
        }
      }
      if (this.map.getLayer('day-path-layer')) this.map.setLayoutProperty('day-path-layer', 'visibility', 'visible');
      for (let i = 0; i < 5; i++) {
        const lid = `future-route-layer-${i}`;
        if (this.map.getLayer(lid)) this.map.setLayoutProperty(lid, 'visibility', 'visible');
      }

      this.positionHistoryCache = {};
      this.historyDate = '';
    },

    onScrubberInput(value) {
      const v = Number(value);
      this.historySliderValue = v;
      const range = this.historyEndTime.getTime() - this.historyStartTime.getTime();
      this.historyTime = new Date(this.historyStartTime.getTime() + (v / 1000) * range);
      // Only move ghost markers (cheap). Path drawing is debounced separately.
      if (!this._ghostRafPending) {
        this._ghostRafPending = true;
        requestAnimationFrame(() => {
          this._ghostRafPending = false;
          this._updateGhostMarkers();
        });
      }
    },

    // Lightweight: only repositions ghost markers — no map layer work
    _updateGhostMarkers() {
      if (!this.map || !this.historyTime) return;
      const vehicles = this.vehicles.filter(v => !v.hiddenFromTracker);
      for (const v of vehicles) {
        const interp = this.interpolatePosition(v.id, this.historyTime);
        if (interp) {
          this.renderGhostMarker(v, interp.lat, interp.lng);
        } else if (this.ghostMarkers[v.id]) {
          this.ghostMarkers[v.id].getElement().style.display = 'none';
        }
      }
      // Debounce path drawing so it only fires after seeking slows/stops
      if (this._pathDrawTimer) clearTimeout(this._pathDrawTimer);
      this._pathDrawTimer = setTimeout(() => { this.drawHistoryPaths(); }, 250);
    },

    // Full render — used after initial load and on explicit calls
    renderHistoryFrame() {
      this._updateGhostMarkers();
      this.drawHistoryPaths();
    },

    interpolatePosition(vehicleId, targetTime) {
      const positions = this.positionHistoryCache[vehicleId];
      if (!positions || positions.length === 0) return null;
      const targetMs = targetTime.getTime();

      let before = null;
      let after = null;
      for (let i = 0; i < positions.length; i++) {
        const t = toDate(positions[i].timestamp).getTime();
        if (t <= targetMs) before = { pos: positions[i], t };
        if (t >= targetMs && !after) after = { pos: positions[i], t };
      }

      if (!before && !after) return null;
      if (!before) return { lat: after.pos.latitude, lng: after.pos.longitude, speed: after.pos.speed || 0 };
      if (!after) return { lat: before.pos.latitude, lng: before.pos.longitude, speed: before.pos.speed || 0 };
      if (before.t === after.t) return { lat: before.pos.latitude, lng: before.pos.longitude, speed: before.pos.speed || 0 };

      const f = (targetMs - before.t) / (after.t - before.t);
      return {
        lat: before.pos.latitude + f * (after.pos.latitude - before.pos.latitude),
        lng: before.pos.longitude + f * (after.pos.longitude - before.pos.longitude),
        speed: before.pos.speed || 0
      };
    },

    renderGhostMarker(vehicle, lat, lng) {
      if (this.ghostMarkers[vehicle.id]) {
        this.ghostMarkers[vehicle.id].setLngLat([lng, lat]);
        this.ghostMarkers[vehicle.id].getElement().style.display = '';
        return;
      }

      const wrapper = document.createElement('div');
      wrapper.style.cssText = 'width: 40px; height: 40px; cursor: pointer;';

      const dot = document.createElement('div');
      const name = vehicle.assignedUserName || vehicle.name || vehicle.id;
      dot.style.cssText = `
        width: 40px; height: 40px; border-radius: 50%;
        display: flex; align-items: center; justify-content: center;
        font-size: 11px; font-weight: bold;
        border: 2px dashed #22d3ee;
        background: rgba(8, 145, 178, 0.6); color: white;
        box-shadow: 0 0 10px rgba(34, 211, 238, 0.4);
      `;
      dot.textContent = this.initials(name);
      wrapper.appendChild(dot);

      wrapper.addEventListener('click', () => this.centerOnVehicle(vehicle));

      const marker = new mapboxgl.Marker({ element: wrapper, anchor: 'center' })
        .setLngLat([lng, lat])
        .addTo(this.map);
      this.ghostMarkers[vehicle.id] = marker;
    },

    toggleHistoryPlay() {
      if (this.historyLoading) return;
      if (this.historyPlaying) {
        clearInterval(this.historyPlayTimer);
        this.historyPlayTimer = null;
        this.historyPlaying = false;
        // Draw final paths when pausing
        this.drawHistoryPaths();
        return;
      }
      this.historyPlaying = true;
      // If at end, restart from beginning
      if (this.historySliderValue >= 1000) {
        this.historySliderValue = 0;
        this.historyTime = new Date(this.historyStartTime.getTime());
      }
      this.historyPlayTimer = setInterval(() => {
        this.historySliderValue += 2;
        if (this.historySliderValue >= 1000) {
          this.historySliderValue = 1000;
          clearInterval(this.historyPlayTimer);
          this.historyPlayTimer = null;
          this.historyPlaying = false;
          this.drawHistoryPaths();
          return;
        }
        const range = this.historyEndTime.getTime() - this.historyStartTime.getTime();
        this.historyTime = new Date(this.historyStartTime.getTime() + (this.historySliderValue / 1000) * range);
        this._updateGhostMarkers();
      }, 50);
    },

    drawHistoryPaths() {
      this.removeHistoryPath();
      if (!this.map || !this.map.isStyleLoaded() || !this.historyTime) return;

      const vehicles = this.vehicles.filter(v => !v.hiddenFromTracker);
      let pathIndex = 0;
      for (const v of vehicles) {
        const positions = this.positionHistoryCache[v.id];
        if (!positions || positions.length < 2) continue;

        const targetMs = this.historyTime.getTime();
        const coords = [];
        for (const p of positions) {
          const t = toDate(p.timestamp).getTime();
          if (t > targetMs) break;
          if (p.longitude && p.latitude) {
            coords.push([p.longitude, p.latitude]);
          }
        }
        // Add interpolated current position
        const interp = this.interpolatePosition(v.id, this.historyTime);
        if (interp) coords.push([interp.lng, interp.lat]);

        if (coords.length < 2) continue;

        const sourceId = `history-path-${pathIndex}`;
        const layerId = `history-path-layer-${pathIndex}`;
        const data = {
          type: 'Feature',
          geometry: { type: 'LineString', coordinates: coords }
        };

        if (!this.map.getSource(sourceId)) {
          this.map.addSource(sourceId, { type: 'geojson', data });
          this.map.addLayer({
            id: layerId,
            type: 'line',
            source: sourceId,
            paint: {
              'line-color': '#22d3ee',
              'line-width': 2,
              'line-opacity': 0.5,
              'line-dasharray': [3, 2]
            }
          });
        }
        pathIndex++;
      }
      this.historyPathDrawn = true;
      this._historyPathCount = pathIndex;
    },

    removeHistoryPath() {
      if (!this.map) return;
      const count = this._historyPathCount || 50;
      for (let i = 0; i < count; i++) {
        const layerId = `history-path-layer-${i}`;
        const sourceId = `history-path-${i}`;
        if (this.map.getLayer(layerId)) this.map.removeLayer(layerId);
        if (this.map.getSource(sourceId)) this.map.removeSource(sourceId);
      }
      this.historyPathDrawn = false;
    },

    copyTraccarUrl() {
      navigator.clipboard.writeText('http://34.61.85.42:8082');
      this.traccarUrlCopied = true;
      setTimeout(() => { this.traccarUrlCopied = false; }, 2000);
    }
  },

  async mounted() {
    // If team leader, load team members and team leaders before initializing
    if (this.isTeamLeader && this.currentUser) {
      try {
        const [members, leaders] = await Promise.all([
          getTeamMembers(this.currentUser.id),
          getTeamLeaders(),
        ]);
        this.teamMembers = members || [];
        this.allTeamLeaders = leaders || [];
        this.teamMemberIds = this.teamMembers.map(m => m.id);
      } catch (e) {
        console.error('Error loading team data:', e);
      }
    }

    this.initMap();
    this.unsubscribe = pollVehicles((vehicles) => {
      this.onVehiclesUpdate(vehicles);
    }, 10000);

    // Refresh jobs every 60s so completed jobs clear promptly
    this._jobRefreshTimer = setInterval(() => {
      this.vehicles.forEach(v => this.loadJobsForVehicle(v));
    }, 60000);

  },

  beforeDestroy() {
    if (this.unsubscribe) this.unsubscribe();
    if (this._jobRefreshTimer) clearInterval(this._jobRefreshTimer);
    if (this.historyPlayTimer) clearInterval(this.historyPlayTimer);
    if (this._pathDrawTimer) clearTimeout(this._pathDrawTimer);
    Object.values(this.ghostMarkers).forEach(m => m.remove());
    if (this.focusedJob) this.exitJobFocus();
    this.removeDayPath();
    this.removeFutureRoutes();
    if (this.searchMarker) this.searchMarker.remove();
    if (this.geocoder) this.geocoder.onRemove();
    if (this.map) this.map.remove();
  },
};
</script>

<style scoped>
.ml-13 {
  margin-left: 3.25rem;
}
</style>

<style>
/* ── Retro Radio Dial ── */
.radio-dial {
  height: 56px;
  background: #0a0a0a;
  box-shadow: inset 0 2px 8px rgba(0,0,0,0.6), inset 0 -1px 4px rgba(0,0,0,0.4);
  border-bottom: 1px solid #222;
  user-select: none;
}
.radio-dial-inner {
  display: flex;
  align-items: center;
  height: 100%;
  padding: 0 12px;
}
.radio-dial-play-btn {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #1a1a1a;
  border: 1px solid #333;
  color: #d4a03c;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  margin-right: 12px;
  font-size: 12px;
  transition: background 0.2s;
}
.radio-dial-play-btn:hover {
  background: #252525;
  border-color: #d4a03c;
}
.radio-dial-track {
  flex-grow: 1;
  height: 100%;
  position: relative;
  cursor: crosshair;
  overflow: hidden;
  background: linear-gradient(180deg, #080808 0%, #111 50%, #080808 100%);
  border-left: 1px solid #222;
  border-right: 1px solid #222;
}
.radio-tick {
  position: absolute;
  top: 8px;
  width: 1px;
  height: 18px;
  background: rgba(255,255,255,0.2);
  transform: translateX(-50%);
  pointer-events: none;
}
.radio-tick-label {
  position: absolute;
  bottom: 6px;
  transform: translateX(-50%);
  font-size: 9px;
  color: #d4a03c;
  font-family: 'Courier New', monospace;
  pointer-events: none;
  white-space: nowrap;
  opacity: 0.7;
}
.radio-needle {
  position: absolute;
  top: 4px;
  bottom: 4px;
  width: 2px;
  background: #e63946;
  transform: translateX(-50%);
  box-shadow: 0 0 8px rgba(230, 57, 70, 0.7), 0 0 16px rgba(230, 57, 70, 0.3);
  pointer-events: none;
  z-index: 2;
  will-change: left;
}
.radio-needle::before {
  content: '';
  position: absolute;
  top: -4px;
  left: -4px;
  width: 10px;
  height: 10px;
  background: #e63946;
  border-radius: 50%;
  box-shadow: 0 0 6px rgba(230, 57, 70, 0.8);
}
.radio-needle::after {
  content: '';
  position: absolute;
  bottom: -4px;
  left: -4px;
  width: 10px;
  height: 10px;
  background: #e63946;
  border-radius: 50%;
  box-shadow: 0 0 6px rgba(230, 57, 70, 0.8);
}
.radio-dial-right {
  flex-shrink: 0;
  margin-left: 12px;
  text-align: right;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}
.radio-time-display {
  font-family: 'Courier New', monospace;
  font-size: 14px;
  color: #d4a03c;
  text-shadow: 0 0 8px rgba(212, 160, 60, 0.5);
  font-weight: bold;
  letter-spacing: 1px;
}
.history-date-picker {
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 4px;
  color: #d4a03c;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  padding: 2px 6px;
  height: 24px;
  outline: none;
  cursor: pointer;
  color-scheme: dark;
}
.history-date-picker:hover {
  border-color: #d4a03c;
}
.history-date-picker:disabled {
  opacity: 0.4;
  cursor: wait;
}
.radio-live-btn {
  display: flex;
  align-items: center;
  background: none;
  border: none;
  color: #22c55e;
  font-size: 10px;
  font-weight: bold;
  cursor: pointer;
  letter-spacing: 1px;
  margin-top: 2px;
  padding: 2px 0;
  transition: color 0.2s;
}
.radio-live-btn:hover {
  color: #4ade80;
}
.radio-live-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #22c55e;
  margin-right: 4px;
  animation: radio-live-pulse 1.5s ease-in-out infinite;
}
@keyframes radio-live-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}
.radio-dial-loading-overlay {
  position: absolute;
  inset: 0;
  background: rgba(10,10,10,0.85);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 5;
}
.radio-dial-loading-bar {
  width: 60%;
  height: 3px;
  border-radius: 2px;
  background: #1a1a1a;
  overflow: hidden;
  position: relative;
}
.radio-dial-loading-bar::after {
  content: '';
  position: absolute;
  top: 0;
  left: -40%;
  width: 40%;
  height: 100%;
  background: linear-gradient(90deg, transparent, #d4a03c, transparent);
  animation: radio-loading-sweep 1.2s ease-in-out infinite;
}
@keyframes radio-loading-sweep {
  0% { left: -40%; }
  100% { left: 100%; }
}
.radio-dial-loading-text {
  margin-top: 6px;
  font-size: 10px;
  color: #d4a03c;
  font-family: 'Courier New', monospace;
  letter-spacing: 0.5px;
  opacity: 0.8;
}

/* ── Geocoder Backdrop Glow ── */
.geocoder-backdrop {
  padding: 24px;
  border-radius: 16px;
  background: radial-gradient(ellipse at center, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.4) 50%, transparent 80%);
  box-shadow: 0 0 80px 40px rgba(0,0,0,0.5);
}

@keyframes marker-pulse-ring {
  0% {
    transform: scale(1);
    opacity: 0.6;
  }
  100% {
    transform: scale(1.8);
    opacity: 0;
  }
}

.mapboxgl-popup-content {
  border-radius: 8px !important;
  padding: 8px !important;
}

/* Dark theme for Mapbox Geocoder */
.geocoder-container .mapboxgl-ctrl-geocoder {
  width: 100% !important;
  max-width: 100% !important;
  background: rgba(17, 24, 39, 0.95);
  border: 1px solid #374151;
  border-radius: 0.5rem;
  color: #fff;
  font-size: 14px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
}
.geocoder-container .mapboxgl-ctrl-geocoder input {
  color: #fff;
}
.geocoder-container .mapboxgl-ctrl-geocoder input::placeholder {
  color: #6b7280;
}
.geocoder-container .mapboxgl-ctrl-geocoder .mapboxgl-ctrl-geocoder--icon-search {
  fill: #6b7280;
}
.geocoder-container .mapboxgl-ctrl-geocoder .mapboxgl-ctrl-geocoder--button {
  background: transparent;
}
.geocoder-container .mapboxgl-ctrl-geocoder .mapboxgl-ctrl-geocoder--icon-close {
  fill: #9ca3af;
}
.geocoder-container .mapboxgl-ctrl-geocoder .suggestions {
  background: rgba(17, 24, 39, 0.95);
  border: 1px solid #374151;
  border-radius: 0 0 0.5rem 0.5rem;
}
.geocoder-container .mapboxgl-ctrl-geocoder .suggestions > li > a {
  color: #d1d5db;
}
.geocoder-container .mapboxgl-ctrl-geocoder .suggestions > .active > a,
.geocoder-container .mapboxgl-ctrl-geocoder .suggestions > li > a:hover {
  color: #fff;
  background: #374151;
}
</style>
